import CFG from '../gameLoopConfig.json';
import { PLAYER_SIZE, ENEMY_SIZE, ENEMY_DAMAGE } from '../../../constants';
import { calcDamageReceived } from '../helpers';

export function checkLevelUp(s) {
  if (s.xp < s.xpToNext) return;
  s.xp      -= s.xpToNext;
  s.level   += 1;
  s.xpToNext = Math.ceil(s.xpToNext * CFG.combat.xpToNextGrowthRate);
  if (s.lichPactActive) s.hp = Math.max(10, s.hp - 20);
  if (s.solarFlareActive) s.solarFlareReady = true;
}

export function processPlayerEnemyCollision(s, timestamp, pcx, pcy, setGameState) {
  const isShielded = timestamp < (s.shieldUntil ?? 0);
  if (isShielded || s.isDashing || timestamp - s.lastHitTime <= (s.hitCooldown ?? 1500)) return false;

  for (const enemy of s.enemies) {
    const eSize = enemy.size ?? ENEMY_SIZE;
    if (s.player.x >= enemy.x + eSize || s.player.x + PLAYER_SIZE <= enemy.x ||
        s.player.y >= enemy.y + eSize || s.player.y + PLAYER_SIZE <= enemy.y) continue;

    const mod2  = s.activeModifier;
    let rawHit  = enemy.isBoss
      ? Math.round(s.maxHp * CFG.combat.bossHpPct * (mod2?.enemyDamageMult ?? 1))
      : Math.round((enemy.damage ?? ENEMY_DAMAGE) * (mod2?.enemyDamageMult ?? 1));

    const effectiveCritRate = (enemy.critRate ?? 0.05) + (mod2?.enemyCritBonus ?? 0);
    if (Math.random() < effectiveCritRate) rawHit = Math.round(rawHit * (enemy.critDamage ?? 1.3));

    const dtype    = enemy.damageType ?? 'physical';
    const fireRes  = Math.max(0, Math.min(0.75, (s.fireRes ?? 0)      - (mod2?.fireResDown      ?? 0)));
    const lRes     = Math.max(0, Math.min(0.75, (s.lightningRes ?? 0) - (mod2?.lightningResDown ?? 0)));
    const physRes  = Math.max(0, Math.min(0.75, s.resistance ?? 0));
    rawHit = calcDamageReceived(rawHit, dtype, fireRes, lRes, s.armor ?? 0, physRes);

    // Shadow Dodge (legendary ability — also removes the enemy)
    if (s.shadowDodgeChance && Math.random() < s.shadowDodgeChance) {
      s.lastHitTime  = timestamp;
      s.lastDodgeTime = timestamp;
      s.enemies      = s.enemies.filter(e => e.id !== enemy.id);
      s.floatingTexts.push({ id: `ft_dodge_${timestamp}`, x: pcx, y: pcy - 20, text: '🌀 DODGE', color: '#88ccff', startTime: timestamp, duration: 900 });
      break;
    }

    // Dodge Chance (item stat — player slips the hit, enemy survives)
    if (s.dodgeChance && Math.random() < s.dodgeChance) {
      s.lastHitTime   = timestamp;
      s.lastDodgeTime = timestamp;
      s.floatingTexts.push({ id: `ft_dc_${timestamp}`, x: pcx, y: pcy - 20, text: '💨 DODGE', color: '#aaffdd', startTime: timestamp, duration: 900 });
      break;
    }

    s.hp          -= rawHit;
    s.lastHitTime  = timestamp;
    if (!enemy.isTierBoss) s.enemies = s.enemies.filter(e => e.id !== enemy.id);
    s.floatingTexts.push({
      id: `ft_hit_${timestamp}`, x: pcx, y: pcy - 20,
      text: dtype === 'fire' ? `🔥-${rawHit}` : dtype === 'lightning' ? `⚡-${rawHit}` : `-${rawHit}`,
      color: dtype === 'fire' ? '#ff7700' : dtype === 'lightning' ? '#88ccff' : '#ffaaaa',
      startTime: timestamp, duration: 900,
    });

    // Void Pulse
    if (s.voidPulseActive) {
      const PUSH = CFG.combat.voidPulsePush;
      for (const e of s.enemies) {
        const sz2 = e.size ?? ENEMY_SIZE;
        const vx  = (e.x + sz2 / 2) - pcx;
        const vy  = (e.y + sz2 / 2) - pcy;
        const vd  = Math.hypot(vx, vy);
        if (vd >= 5) { e.x += (vx / vd) * PUSH; e.y += (vy / vd) * PUSH; }
      }
      s.spellEffects.push({ id: `se_vp_${timestamp}`, type: 'explosion', x: pcx, y: pcy, radius: CFG.combat.voidPulseRadius, startTime: timestamp, duration: 520 });
    }

    // Death Nova
    if (s.deathNovaActive) {
      let novaKills = 0;
      s.enemies = s.enemies.filter(e => {
        const sz3 = e.size ?? ENEMY_SIZE;
        if (Math.hypot(e.x + sz3 / 2 - pcx, e.y + sz3 / 2 - pcy) < CFG.combat.deathNovaRadius) { novaKills++; return false; }
        return true;
      });
      if (novaKills > 0) { s.kills = (s.kills ?? 0) + novaKills; s.xp += novaKills * s.xpPerKill; }
      s.spellEffects.push({ id: `se_dn_${timestamp}`, type: 'explosion', x: pcx, y: pcy, radius: CFG.combat.deathNovaRadius, startTime: timestamp, duration: 640 });
    }

    if (s.hp <= 0) { s.alive = false; setGameState('dead'); return true; }
    break;
  }
  return false;
}
